import { useRouter } from "next/router";
import { Accordion, Anchor, Button } from "@newsela/angelou";
import { omitBy, isNil } from "@s-libs/micro-dash";

import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import CleverAnchor from "@/components/CleverAnchor/CleverAnchor";
import GoogleAnchor from "@/components/GoogleAnchor/GoogleAnchor";
import MicrosoftAnchor from "@/components/MicrosoftAnchor/MicrosoftAnchor";
import CanvasIcon from "@/icons/CanvasIcon";
import ClassLinkIcon from "@/icons/ClassLinkIcon";
import ExternalLinkIcon from "@/icons/ExternalLinkIcon";
import SchoologyIcon from "@/icons/SchoologyIcon";
import { MONOLITH_ORIGIN } from "@/monolithIntegration/monolithIntegration.constants";
import { isTrackingParam } from "@/utils/appendTrackingParamsToUrl";
import { SupportChat } from "@/login/SupportChat/SupportChat";
import {
  contentContainerCss,
  containerCss,
  paragraphContainerCss,
  headerContainerCss,
  paragraphCss,
  subHeaderCss,
  headerParagraphCss,
  accordionCss,
  accordionAnchorCss,
  accordionContentCss,
} from "./LogInProviders.style";

const AnchorWithTrackEvent = withTrackEvent(Anchor);

const OTHER_LMS_SIGN_IN_ITEMS = [
  {
    name: "Schoology",
    title: "Sign in from Schoology",
    link: "https://app.schoology.com/login",
    icon: SchoologyIcon,
    qaSelector: "sign-in-from-schoology",
  },
  {
    name: "ClassLink",
    title: "Sign in from ClassLink",
    link: "https://launchpad.classlink.com/login",
    icon: ClassLinkIcon,
    qaSelector: "sign-in-from-classlink",
  },
  {
    name: "Canvas",
    title: "Sign in from Canvas",
    link: "https://www.instructure.com/canvas/login",
    icon: CanvasIcon,
    qaSelector: "sign-in-from-canvas",
  },
];

const getSingleQueryValue = (value) =>
  Array.isArray(value) ? value[0] : value;

const appendQueryParam = (searchParams, key, value) => {
  if (value == null) return;

  if (Array.isArray(value)) {
    value.forEach((currentValue) => {
      searchParams.append(key, String(currentValue));
    });
    return;
  }

  searchParams.append(key, String(value));
};

const LogInProviders = () => {
  const { query } = useRouter();
  const { classroom_id: classroomId, next } = query;
  const buildProviderParams = (source) => {
    const searchParams = new URLSearchParams(
      omitBy(
        {
          source,
          classroom_id: getSingleQueryValue(classroomId),
          destination_url: getSingleQueryValue(next),
        },
        isNil,
      ),
    );

    Object.entries(query).forEach(([key, value]) => {
      if (!isTrackingParam(key)) return;
      appendQueryParam(searchParams, key, value);
    });

    return searchParams;
  };

  const googleParams = buildProviderParams("sign-in-without-class-code");
  const microsoftParams = buildProviderParams("sign-in");
  const cleverParams = buildProviderParams("sign-in-without-class-code");

  return (
    <div css={containerCss}>
      <div css={headerContainerCss}>
        <h2 css={subHeaderCss}>Sign in through a provider</h2>
        <p css={headerParagraphCss}>
          Already have classes set up? Sync your classes by signing in through
          that provider.
        </p>
      </div>
      <div css={contentContainerCss}>
        <GoogleAnchor
          href={`${MONOLITH_ORIGIN}/login/google-oauth2/?${googleParams.toString()}`}
        >
          Sign in with Google
        </GoogleAnchor>
        <CleverAnchor
          href={`${MONOLITH_ORIGIN}/login/clever/?${cleverParams.toString()}`}
        >
          Sign in with Clever
        </CleverAnchor>
        <MicrosoftAnchor
          href={`${MONOLITH_ORIGIN}/login/microsoft-oauth2/?${microsoftParams.toString()}`}
        >
          Sign in with Microsoft
        </MicrosoftAnchor>
        <Accordion title="Sign in through another LMS" __cssFor={accordionCss}>
          {OTHER_LMS_SIGN_IN_ITEMS.map(
            ({ name, title, link, icon: Icon, qaSelector }) => (
              <AnchorWithTrackEvent
                key={title}
                __cssFor={accordionAnchorCss}
                href={link}
                openInNewTab
                icon={{
                  __customOpenInNewTabIcon: ExternalLinkIcon,
                  removeTextUnderline: true,
                }}
                styleAnchorAsButton={{
                  isFullWidth: true,
                  flavor: Button.flavors.muted,
                }}
                objectType={objectTypes.user}
                actionName={actionNames.select}
                streamProperties={{
                  description: `Select the button allowing the user to sign in with ${name}.`,
                }}
                dataProps={{ "data-qa-selector": qaSelector }}
              >
                <div css={accordionContentCss}>
                  <div css={accordionContentCss.iconContainer}>
                    <Icon />
                  </div>
                  <span>{title}</span>
                </div>
              </AnchorWithTrackEvent>
            ),
          )}
        </Accordion>
      </div>
      <div css={paragraphContainerCss}>
        <span css={paragraphCss}>
          Having sign in issues? Reach out to your IT Admin or explore our{" "}
          <Anchor openInNewTab href="https://support.newsela.com/">
            Support Center
          </Anchor>
          .
        </span>
        <SupportChat />
      </div>
    </div>
  );
};

export default LogInProviders;
